<template>
  <div
    :key="eventState"
    class="main-wrapper"
  >
    <div
      v-if="!selectedEvent"
    >
      <v-skeleton-loader
        type="card-avatar, article, actions"
      />
    </div>
    <div
      v-else
    >
      <EventSelector
        :events="allEvents"
        :selected-id="selectedEvent.id"
        @change="switchEvent"
      />
      <v-row>
        <v-col
          cols="12"
        >
          <Details
            :selected-event="selectedEvent"
            :current-user="currentUser"
          />
        </v-col>
      </v-row>
      <v-row>
        <Prizes
          :current-user="currentUser"
        />
        <Giveaways
          v-if="(registeredToATeam || isAdmin) && areHacking"
          :current-user="currentUser"
        />
        <ScheduleCallToAction
          v-if="presentationTimeSeconds !== undefined"
        />
      </v-row>
      <TeamsList
        :current-user="currentUser"
        :selected-event="selectedEvent"
      />
    </div>
  </div>
</template>

<script>
/* eslint-disable import/no-cycle */
import { v4 as uuidv4 } from 'uuid';
import EventsClient from '@/api/EventsClient';
import { mapActions, mapGetters } from 'vuex';
import AuthClient from '@/api/AuthClient';
import SharedWorkerHelper from '@/workers/sharedWorkerHelper';
import WorkerHelper from '@/workers/workerHelper';
import ScheduleCallToAction from '@/components/ScheduleCallToAction.vue';
import Details from './Details.vue';
import EventSelector from './EventSelector.vue';
import Giveaways from './Giveaways.vue';
import Prizes from './Prizes.vue';
import TeamsList from './TeamsList.vue';

export default {
  name: 'Main',
  components: {
    Details,
    EventSelector,
    Giveaways,
    Prizes,
    TeamsList,
    ScheduleCallToAction,
  },
  props: {
    currentUser: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      allEvents: [],
      currentRefreshInterval: 300000, // 5 mins
      desiredEvent: null,
      eventState: '',
      isFirstLoad: true,
      selectedEvent: null,
      uuid: null,
      worker: null, // for token refresh
    };
  },
  computed: {
    ...mapGetters({
      currentJwtToken: 'currentJwtToken',
      currentRefreshToken: 'currentRefreshToken',
      isAuthorized: 'isAuthorized',
      presentationTimeSeconds: 'presentationTimeSeconds',
    }),
    areHacking() {
      return this.$store.state.eventState === 'Hacking'
        || this.$store.state.eventState === 'Judging'
        || this.$store.state.eventState === 'PrizeSelection'
        || this.$store.state.eventState === 'Completed';
    },
    haveActiveWorker() {
      return !!this.worker;
    },
    isAdmin() {
      return this.currentUser.isAdmin;
    },
    registeredToATeam() {
      return this.currentUser.team !== '';
    },
    supportsSharedWorkers() {
      return !!window.SharedWorker;
    },
    supportsWebWorkers() {
      return !!window.Worker;
    },
  },
  watch: {
    desiredEvent: {
      async handler(newValue, oldValue) {
        if (newValue !== oldValue) {
          await this.getEventData();
        }
      },
      // force eager callback execution
      immediate: true,
    },
  },
  created() {
    this.createUuid();
    try {
      this.handleWorkerEnd();
    } catch (e) {
      // do nothing, we have no straggling workers
    }
    this.createWorker();
    if (this.currentRefreshInterval > 0 && this.isAuthorized) {
      this.setupWorkerActions();
      this.handleWorkerStart();
    }
  },
  beforeMount() {
    if (this.$route.params.eventId && !this.isAuthorized) {
      this.$router.push('/login');
    }
    try {
      const afterLogin = new Promise(() => {
        this.logUserIn();
      });
      afterLogin.then(this.getEventData());
    } catch (err) {
      this.$router.push('/login');
      console.log(err);
    }
  },
  methods: {
    ...mapActions({
      setEvent: 'setEvent',
      setJwtToken: 'setJwtToken',
      setLoggedIn: 'setLoggedIn',
      setRefreshToken: 'setRefreshToken',
      setUserTeam: 'setUserTeam',
    }),
    createWorker() {
      if (!this.worker) {
        if (this.supportsSharedWorkers) {
          this.worker = new SharedWorkerHelper(this.uuid);
        } else if (this.supportsWebWorkers) {
          this.worker = new WorkerHelper(this.uuid);
        } else {
          console.log('web workers not supported in this browser');
        }
      }
    },
    createUuid() {
      this.uuid = uuidv4();
    },
    async getEventData() {
      try {
        const eventsClient = await new EventsClient(this.currentJwtToken);
        const responseData = await eventsClient.getEvents();
        this.allEvents = responseData;
        const match = this.allEvents.find((e) => e.id === this.$route.params.eventId);
        this.selectedEvent = match || this.allEvents[0];
      } catch (err) {
        this.triggerLogout();
      }
      this.eventState = this.selectedEvent?.state || 'Setup';
      if (this.selectedEvent !== undefined && this.selectedEvent !== null) {
        this.setEvent(this.selectedEvent);
        if (this.$route.params?.eventId !== this.selectedEvent?.id) {
          this.switchEvent(this.selectedEvent?.id || null);
        }
      }
      this.$emit('refreshed-auth-status');
    },
    async logUserIn() {
      if (this.currentJwtToken && this.currentUser) {
        if (this.currentUser.name) {
          // we are already authenticated, update tokens
          await this.refreshAuth();
          this.setLoggedIn(true);
        }
      } // we just always want to keep the session active
    },
    handleWorkerUpdate(type) {
      if (type === 'refreshTokens') {
        this.refreshAuth();
      }
    },
    async handleWorkerStart() {
      await this.createWorker();
      await this.setupWorkerActions();
      this.worker.on('setInterval', () => {
        this.handleWorkerUpdate('refreshTokens');
      });
    },
    handleWorkerEnd() {
      if (this.worker) {
        this.worker.terminate();
        this.worker = null;
      }
    },
    setupWorkerActions() {
      this.worker.trigger('setInterval', {
        interval: this.currentRefreshInterval,
        type: 'refreshTokens',
        appUuid: this.uuid,
      });
    },
    async triggerLogout() {
      this.setLoggedIn(false);
      this.handleWorkerEnd();
      this.$store.commit('setLoggedIn', false);
      await this.$store.dispatch('logOutUser');
      this.$router.push('/login');
    },
    async refreshAuth() {
      if (this.isAuthorized && (this.currentJwtToken && this.currentRefreshToken)) {
        try {
          const redirectUri = process.env.VUE_APP_HACKATHON_REDIRECT_URI;
          const authClient = new AuthClient();
          const result = await authClient.refreshTokens(this.currentRefreshToken, redirectUri);
          const { accessToken, refreshToken } = result;
          if (accessToken === undefined || refreshToken === undefined) {
            this.triggerLogout();
          }
          this.setJwtToken(accessToken);
          this.setRefreshToken(refreshToken);
        } catch (err) {
          console.log('Authentication error, please log in again');
          this.triggerLogout();
        }
      }
    },
    switchEvent(eventId) {
      this.$router.push(`/${eventId}`);
    },
  },
  async beforeRouteUpdate(to, from, next) {
    if ((to.params.eventId !== this.desiredEvent) && this.currentJwtToken !== null) {
      this.selectedEvent = null;
      this.desiredEvent = to.params?.eventId || null;
      this.setUserTeam('');
      next();
    } else {
      this.$router.push('/login');
    }
  },
};
</script>

<style lang="scss" scoped>
.main-wrapper {
  background: white;
  padding: 1em;
}
</style>
